import React, { useEffect, useRef, useState } from "react";

import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/Modal";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { Check, Copy, Eye, Link2, ShieldCheck, X } from "lucide-react";
import toast from "react-hot-toast";

import { ComplianceService } from "@/services/complianceService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import Select from "@/components/ui/Select";
import { Input } from "@/components/ui/Input";
import useModuleAccess from "@/hooks/useModuleAccess";
import { MODULES } from "@/permissions/modules";

/**
 * Send-to-Compliance modal — step 1 of two: which checklist.
 *
 * Picks a checklist for a recruitment candidate. What happens next depends on
 * whether the tenant can send outreach email.
 *
 * **With `outreach.email`** (the ordinary case): this modal says **Next** and
 * calls nothing. It hands `{checklistId}` up to the page, which opens
 * `ComplianceEmailModal` — step 2, where the recruiter reviews the email the
 * candidate receives and sends it. The portal-link panel lives there now,
 * because that is where the send happens.
 *
 * **Without it** (Q-NoOutreach): this modal keeps today's one-step send exactly
 * as it was — `sendFromRecruitment` with no `subject`/`body`, which the server
 * preserves as the old `EmailTemplate` path — and **keeps the portal-link copy
 * panel**. Without outreach the recruiter is the only delivery path the
 * candidate has, so removing that link would strand them. Never a dead button:
 * a tenant that cannot reach step 2 is never shown a `Next` that leads nowhere.
 *
 * Note: the checklist dropdown is backed by a Phase D endpoint that is not built
 * yet, so the picker may be empty until then (see complianceService.js).
 */
const SendToComplianceModal = ({ open, onClose, candidate, applicationId, jobId, jobTitle, onSent, onViewChecklist, onNext }) => {
  const [checklists, setChecklists] = useState([]);
  const [checklistId, setChecklistId] = useState("");
  const [loadingChecklists, setLoadingChecklists] = useState(false);
  // Phase E. What the job says its candidates get by default, resolved once on
  // open. `null` means "asked, and the job has none" — the ordinary case, not
  // an error — and the modal then behaves exactly as it did before.
  const [resolved, setResolved] = useState(null);
  const [resolving, setResolving] = useState(false);
  // The recruiter asked to pick something else. A job default is a DEFAULT:
  // candidates on one job do not all need the same evidence, so this is a
  // required escape hatch, not a nicety.
  const [overriding, setOverriding] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [portalUrl, setPortalUrl] = useState("");
  const [copied, setCopied] = useState(false);
  // A refusal the officer has to read, rather than a toast that vanishes.
  // Ownership and stage-ineligibility are both real refusals, and both are
  // 409s — keeping the modal open is the only way the reason survives.
  const [refusal, setRefusal] = useState("");
  const portalInputRef = useRef(null);

  // Whether step 2 exists at all. `canUse` is entitlement AND availability:
  // a tenant that never bought outreach email and a fleet-wide outage both
  // mean the email composer cannot send, so both fall back to the one-step
  // path rather than to a Next that leads to a Send the route would refuse.
  const { canUse: canComposeEmail } = useModuleAccess(MODULES.OUTREACH_EMAIL);
  // `onNext` is what actually hosts step 2. A host that has not wired it gets
  // the one-step send, not a button that silently does nothing.
  const twoStep = canComposeEmail && !!onNext;

  // Load checklist options each time the modal opens.
  useEffect(() => {
    if (!open) return;

    setChecklistId("");
    setSubmitting(false);
    setPortalUrl("");
    setCopied(false);
    setRefusal("");
    setResolved(null);
    setOverriding(false);
    setLoadingChecklists(true);

    let active = true;
    ComplianceService.getChecklistDropdown()
      .then((options) => {
        if (active) setChecklists(options);
      })
      .catch((e) => {
        console.error(e);
        if (active) setChecklists([]);
        toast.error(apiErrorMessageFormat(e, "Failed to load checklists"));
      })
      .finally(() => {
        if (active) setLoadingChecklists(false);
      });

    return () => {
      active = false;
    };
  }, [open]);

  // Phase E. Ask the job what its candidates get, once per open.
  //
  // A convenience, never a gate: a failure or a job with no default both land
  // on today's manual picker. Losing the ability to send someone because a
  // lookup failed would be a worse screen than the one this replaces.
  useEffect(() => {
    if (!open || !jobId) return undefined;

    let active = true;
    setResolving(true);
    ComplianceService.getJobChecklist(jobId)
      .then((res) => {
        if (!active) return;
        // `checklistId` is null when the job has no default, or when its
        // default was since deleted or retired. All three mean "ask".
        setResolved(res?.checklistId ? res : null);
        if (res?.checklistId) setChecklistId(res.checklistId);
      })
      .catch((e) => {
        console.error(e);
        if (active) setResolved(null);
      })
      .finally(() => {
        if (active) setResolving(false);
      });

    return () => {
      active = false;
    };
  }, [open, jobId]);

  // Which of the two bodies renders. The confirm view needs a resolved
  // checklist AND the recruiter not to have asked for the picker.
  const showConfirm = Boolean(resolved?.checklistId) && !overriding;

  const canSubmit = !!checklistId && !submitting && !!candidate?.id;

  const handleCopy = async () => {
    // navigator.clipboard only exists in a secure context (HTTPS or
    // localhost). On a plain-HTTP LAN host it's undefined and the async
    // API throws, so fall back to selecting the input + execCommand("copy")
    // which works in insecure contexts too.
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(portalUrl);
        ok = true;
      }
    } catch {
      ok = false;
    }
    if (!ok) {
      try {
        const el = portalInputRef.current;
        if (el) {
          el.focus();
          el.select();
          el.setSelectionRange(0, portalUrl.length);
          ok = document.execCommand("copy");
          el.setSelectionRange(0, 0);
          el.blur();
        }
      } catch {
        ok = false;
      }
    }
    if (ok) {
      setCopied(true);
      toast.success("Portal link copied");
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Could not copy — please select the link and copy it manually.");
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    // Step 1 of two writes nothing. The checklist goes up to the page, which
    // opens step 2 as a SIBLING dialog — never nested in this one's content,
    // which would create a stacking context it can never paint above (React
    // error #299), the same trap `onViewChecklist` below records.
    if (twoStep) {
      onNext({ checklistId });
      return;
    }

    setSubmitting(true);
    try {
      const res = await ComplianceService.sendFromRecruitment({
        candidateId: candidate.id,
        checklistId,
        applicationId,
      });

      toast.success("Candidate sent to compliance");
      onSent?.(candidate.id);

      // Phase K (D10): keep the modal open to surface the copyable
      // candidate portal link; the user closes it via Done.
      if (res?.portalUrl) {
        setPortalUrl(res.portalUrl);
        return;
      }
      onClose?.();
    } catch (e) {
      console.error(e);

      // Branch on `error_code`, never on the status alone. This route raises
      // THREE different 409s, and only one of them means the candidate is in
      // compliance:
      //
      //   COMPLIANCE_CANDIDATE_EXISTS  — they are; relabel and close.
      //   ..._RUN_OWNED_BY_ANOTHER_APPLICATION — another job owns the run.
      //   ..._STAGE_NOT_ELIGIBLE       — they never reached the trigger status.
      //
      // Treating the last two as success is what the old `status === 409`
      // check did: it called `onSent` and relabelled the row "In Compliance"
      // for a candidate who was never sent.
      const code = e?.response?.data?.error_code;
      if (code === "COMPLIANCE_CANDIDATE_EXISTS") {
        toast(apiErrorMessageFormat(e, "Candidate is already in compliance"));
        onSent?.(candidate.id);
        onClose?.();
        return;
      }
      if (e?.response?.status === 409) {
        // A real refusal. The server's message already names the owning job
        // (or the stage the tenant starts at), and it is the only explanation
        // the recruiter gets — so it stays on screen.
        setRefusal(apiErrorMessageFormat(e, "This candidate cannot be sent to compliance right now."));
        return;
      }

      toast.error(apiErrorMessageFormat(e, "Failed to send candidate to compliance"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose?.();
      }}
    >
      <DialogContent className="w-[480px] md:w-full flex flex-col p-0">
        {/* Header */}
        <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0">
          <div>
            <DialogTitle className="text-[15px] font-semibold text-text-primary">Send to Compliance</DialogTitle>

            <DialogDescription className="text-[12px] text-text-primary/70 mt-0.5">
              {/* Two flows, two promises. Saying "email document instructions"
                in front of a Next that opens an editor names an action this
                screen no longer performs. */}
              {twoStep
                ? candidate?.full_name
                  ? `Choose what ${candidate.full_name} needs to provide. You write the email next.`
                  : "Choose what the candidate needs to provide. You write the email next."
                : candidate?.full_name
                  ? `Assign a checklist for ${candidate.full_name} and email document instructions.`
                  : "Assign a checklist and email document instructions."}
            </DialogDescription>
          </div>

          {/* Close button */}
          <DialogClose className="cursor-pointer absolute right-5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center">
            <X size={16} strokeWidth={3} />
          </DialogClose>
        </DialogHeader>

        {/* Body — after a successful send, show the copyable portal link (D10). */}
        {portalUrl ? (
          <div className="px-5 py-4" style={{ zoom: 0.85 }}>
            <div className="flex items-center gap-2 text-[13px] font-medium text-text-primary mb-1.5">
              <Link2 size={15} />
              Candidate upload portal link
            </div>
            <p className="text-[12px] text-text-primary/70 mb-2">Share this link with the candidate — they can upload their documents directly, no login needed.</p>

            <div className="flex items-center gap-2">
              <Input
                ref={portalInputRef}
                type="text"
                readOnly
                value={portalUrl}
                onFocus={(e) => e.target.select()}
                className="flex-1 min-w-0 rounded-xl bg-background-icon/40 border border-border-primary px-3 py-2 text-[12px] text-text-primary outline-none"
              />
              <Button type="button" variant="outline" onClick={handleCopy} className="shrink-0">
                {copied ? <Check size={15} /> : <Copy size={15} />}
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button className="bg-button-secondary text-text-base" type="button" onClick={() => onClose?.()}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <div className="px-5 py-4" style={{ zoom: 0.85 }}>
            {resolving ? (
              <p className="py-6 text-center text-[13px] text-text-primary/60">Checking what this job asks for…</p>
            ) : showConfirm ? (
              /* Phase E. The job already answered the question, so stop asking
                it. The checklist is named, viewable and changeable — and Send
                is one click. */
              <>
                <label className="block text-[13px] font-medium text-text-primary mb-1.5">Checklist</label>

                <div className="flex items-center gap-2 rounded-xl border border-border-primary bg-background-icon/40 px-3 py-2">
                  <ShieldCheck size={15} className="shrink-0 text-text-primary/70" />
                  <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-text-primary">{resolved.checklistName}</span>

                  {/* Hands the id UP rather than mounting a dialog in here: a
                    dialog inside another's content creates a stacking context
                    it can never paint above (React error #299). The page owns
                    the read-only builder, exactly as job-detail's card does. */}
                  {onViewChecklist && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon-sm"
                      aria-label="View checklist"
                      title="View this checklist"
                      className="shrink-0"
                      onClick={() => onViewChecklist(resolved.checklistId)}
                    >
                      <Eye size={14} />
                    </Button>
                  )}
                </div>

                <p className="mt-1.5 text-[12px] text-text-primary/60">
                  {jobTitle ? `The default for ${jobTitle}.` : "This job's default."} It is a starting point — change it if this candidate needs different
                  evidence.
                </p>

                <div className="mt-2">
                  <Button
                    type="button"
                    className="h-7 bg-button-secondary px-2.5 text-[12px] text-text-base"
                    disabled={submitting}
                    onClick={() => setOverriding(true)}
                  >
                    Change
                  </Button>
                </div>
              </>
            ) : (
              <>
                <label className="block text-[13px] font-medium text-text-primary mb-1.5">Checklist</label>

                <Select
                  value={checklistId}
                  onChange={setChecklistId}
                  options={checklists}
                  placeholder={loadingChecklists ? "Loading checklists…" : "Select a checklist"}
                  disabled={loadingChecklists || submitting}
                  className="rounded-xl bg-background-icon/40 px-3 py-2 text-[13px] disabled:opacity-50"
                  // An eye on EVERY option: choosing a checklist for someone
                  // means knowing what it asks them for, and without this the
                  // recruiter has to leave for the compliance screen and come
                  // back. Reading one must not select it — `Select` stops the
                  // propagation so this stays a preview.
                  optionAction={
                    onViewChecklist
                      ? (option) => (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`View ${option.label}`}
                            title={`View ${option.label}`}
                            onClick={() => onViewChecklist(option.value)}
                          >
                            <Eye size={14} />
                          </Button>
                        )
                      : undefined
                  }
                />

                {!loadingChecklists && checklists.length === 0 && <p className="text-[12px] text-text-primary/60 mt-1.5">No checklists available yet.</p>}

                {/* Why they are being asked. Without this the picker looks
                  identical whether the job has no default or the lookup
                  failed, and neither is the recruiter's fault. */}
                {!loadingChecklists && checklists.length > 0 && jobId && !resolved && (
                  <p className="mt-1.5 text-[12px] text-text-primary/60">This job has no checklist assigned — choose one for this candidate.</p>
                )}
              </>
            )}

            {/* Shared by BOTH branches. A refusal is the only explanation the
              recruiter gets, and the send can be refused from either one —
              rendering it inside the picker alone would throw it away for
              every confirm-view send. */}
            {refusal && <div className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-2.5 text-[12px] text-amber-700">{refusal}</div>}

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-6">
              <DialogClose
                render={
                  <Button variant="outline" className="border-none" type="button" disabled={submitting}>
                    Cancel
                  </Button>
                }
              />

              <Button className="bg-button-secondary text-text-base" type="button" onClick={handleSubmit} disabled={!canSubmit || resolving}>
                {twoStep ? "Next" : submitting ? "Sending…" : "Send to Compliance"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SendToComplianceModal;
